/**
 * Time utility functions for parsing and converting time strings.
 */

/**
 * Converts a time string in HH:MM format to total minutes.
 *
 * @param time - Time string in HH:MM format (e.g., "09:30", "14:00")
 * @returns Total minutes from midnight, or 0 for invalid/malformed inputs
 *
 * @example
 * timeToMinutes("09:30") // Returns 570 (9 * 60 + 30)
 * timeToMinutes("14:00") // Returns 840 (14 * 60)
 * timeToMinutes("invalid") // Returns 0 (safe fallback)
 * timeToMinutes("25:99") // Returns 0 (invalid time)
 */
export function timeToMinutes(time: string): number {
  // Validate input is a string
  if (typeof time !== 'string') {
    return 0;
  }

  // Split on ':' and validate we have exactly 2 parts
  const parts = time.split(':');
  if (parts.length !== 2) {
    return 0;
  }

  // Parse hours and minutes
  const hours = parseInt(parts[0], 10);
  const minutes = parseInt(parts[1], 10);

  // Guard against NaN or invalid values
  if (Number.isNaN(hours) || Number.isNaN(minutes)) {
    return 0;
  }

  // Validate time range (hours: 0-23, minutes: 0-59)
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
    return 0;
  }

  // Calculate total minutes
  return hours * 60 + minutes;
}
